import { TableV2 as Table } from "@asayinc/component-library";
import { FilterList } from "@mui/icons-material";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { TableMultiSelectFilter } from "src/components/molecules/TableMultiSelectFilter";
import { ROW_OPTIONS } from "src/constants/options/rowOptions";
import { SHARE_RANGE_ANALYSIS_TYPE_OPTIONS } from "src/constants/options/shareRangeAnalysisOptions";
import { useTablePagination } from "src/hooks/useTablePagination";
import { useShareRangeAnalysisEventsQuery } from "src/store/shareRangeAnalysisEvent";
import {
  getColumnData,
  getRowData,
  SHARE_RANGE_ANALYSIS_EVENTS_COLUMNS,
} from "./utils";
import { IShareRangeAnalysisEvent } from "src/store/shareRangeAnalysisEvent/types";
import { TABLE_SEARCH_PARAMS } from "src/constants/tableSearchParams";
import { useTableSearchParams } from "src/hooks/useTableSearchParams";
import { ScrollContainer } from "src/components/containers/ScrollContainer";

export function IssuerShareRangeAnalysisEvents() {
  const { id: issuerId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const methods = useForm();
  const view = searchParams.get(TABLE_SEARCH_PARAMS.VIEW) || undefined;
  const { type } = useTableSearchParams();

  const {
    getSort,
    offset,
    limit,
    ordering,
    onChangePage,
    onChangeRows,
    onOrderingChange,
  } = useTablePagination();
  const { data, isFetching, isLoading } = useShareRangeAnalysisEventsQuery({
    offset,
    limit,
    ordering,
    type,
    issuerId,
  });

  const paginateProps = {
    onChangePage,
    onChangeRows,
    rowOptions: ROW_OPTIONS,
    count: data?.count || 0,
    page: Math.floor(offset / limit) + 1,
    rowsPerPage: limit,
  };

  const handleRowClick = (event: IShareRangeAnalysisEvent) => {
    const { id } = event;
    navigate(`/share-range-analysis-events/${id}/details`);
  };

  const columns = useMemo(() => SHARE_RANGE_ANALYSIS_EVENTS_COLUMNS, []);
  const columnData = useMemo(() => {
    return getColumnData({
      getSort,
      onOrderingChange,
    });
  }, [getSort, onOrderingChange]);
  const rowData = getRowData(data?.results || [], handleRowClick);

  const filterList = (
    <FormProvider {...methods}>
      <TableMultiSelectFilter
        fieldName={TABLE_SEARCH_PARAMS.TYPE}
        label="Types"
        options={SHARE_RANGE_ANALYSIS_TYPE_OPTIONS}
        queryParameter={TABLE_SEARCH_PARAMS.TYPE}
        view={view}
      />
    </FormProvider>
  );

  return (
    <ScrollContainer sx={{ pb: 10, pl: 10, pr: 6.25 }}>
      <Table
        count={data?.count || 0}
        columnData={columnData}
        columns={columns}
        isFetching={isFetching}
        isLoading={isLoading}
        noResultsData={{
          title: "No Share Range Analysis Events found",
          subtitle:
            "Sorry, there are no Share Range Analysis Events that match your applied filters.",
        }}
        paginateProps={paginateProps}
        rows={rowData}
        tableMinWidth={1100}
        title="Share Range Analysis Events"
        titleVariant="subtitle2"
        toolbarInside
        toolbarCollapse={{
          Icon: FilterList,
          collapseContent: filterList,
          isFlat: true,
        }}
      />
    </ScrollContainer>
  );
}
